// client/src/components/OpportunityGroupChat.jsx
import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useAuth } from "../context/AuthContext";

const SOCKET_URL = process.env.REACT_APP_API_URL;

export default function OpportunityGroupChat({ opportunityId, onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [chatMode, setChatMode] = useState("announcement"); // default server-provided
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isPoster, setIsPoster] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState("");

  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!opportunityId) return;
    let mounted = true;

    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("cp_token");
        const res = await fetch(`${SOCKET_URL}/api/opportunities/${opportunityId}/group-chat`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await res.json();
        if (!res.ok) {
          console.warn("Group chat fetch failed", d.error);
          return;
        }
        if (!mounted) return;
        setMessages(d.messages || []);
        setChatMode(d.chatMode || "announcement");

        // determine poster/member status by querying opportunity
        const res2 = await fetch(`${SOCKET_URL}/api/opportunities/${opportunityId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const dd = await res2.json();
        if (res2.ok && dd.item) {
          const item = dd.item;
          setIsPoster(String(item.postedBy) === String(user?.id));
          const selectedIds = Array.isArray(item.selected) ? item.selected.map(s => String(s)) : [];
          setIsMember(String(item.postedBy) === String(user?.id) || selectedIds.includes(String(user?.id)));
        }
      } catch (err) {
        console.error("Group chat load error", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    // socket join
    socketRef.current = io(SOCKET_URL, { transports: ["websocket"] });

    socketRef.current.on("connect", () => {
      // auth for socket
      if (user?.id) socketRef.current.emit("auth", { userId: user.id });
      // join group room
      socketRef.current.emit("join_group_room", { room: `group_recruit_${opportunityId}` });
    });

    socketRef.current.on("new_message", ({ message }) => {
      if (!message) return;
      setMessages(prev => [...prev, message]);
    });

    socketRef.current.on("opportunity_chat_mode_changed", ({ postId, chatMode: mode }) => {
      if (String(postId) === String(opportunityId)) {
        setChatMode(mode);
      }
    });

    return () => {
      mounted = false;
      socketRef.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId, user?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${SOCKET_URL}/api/opportunities/${opportunityId}/group-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text, attachmentUrl: attachmentUrl || null }),
      });
      const d = await res.json();
      if (!res.ok) {
        // alert(d.error || "Send failed");
        return;
      }
      // server emits new_message but add locally for instant feedback
      const localMsg = {
        sender: user.id,
        senderName: user.name || "",
        senderEmail: user.email || "",
        text: text.trim(),
        timestamp: new Date(),
        attachmentUrl: attachmentUrl || null,
      };
      setMessages(prev => [...prev, localMsg]);
      setText("");
      setAttachmentUrl("");
    } catch (err) {
      console.error("Send error", err);
    } finally {
      setSending(false);
    }
  };

  const toggleMode = async () => {
    if (!isPoster) return;
    const newMode = chatMode === "announcement" ? "open" : "announcement";
    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${SOCKET_URL}/api/opportunities/${opportunityId}/chat-mode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mode: newMode }),
      });
      const d = await res.json();
      if (!res.ok) {
        // alert(d.error || "Failed to update mode");
        return;
      }
      setChatMode(d.chatMode || newMode);

      // notify local socket room (server already emits notifications)
      socketRef.current?.emit("opportunity_chat_mode_changed", { postId: opportunityId, chatMode: d.chatMode || newMode });
    } catch (err) {
      console.error("Toggle mode error", err);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-3xl mx-auto p-4 bg-white dark:bg-slate-800 rounded shadow">
        Loading group chat…
      </div>
    );
  }

  if (!isMember && !isPoster) {
    return (
      <div className="w-full max-w-3xl mx-auto p-4 bg-white dark:bg-slate-800 rounded shadow">
        You are not a member of this group's chat.
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl mx-auto p-4 bg-white dark:bg-slate-800 rounded shadow">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold">Opportunity Group Chat</h3>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500">Mode: <strong className="ml-1">{chatMode === "announcement" ? "Announcement only" : "Open chat"}</strong></div>
          {isPoster && (
            <button onClick={toggleMode} className="px-2 py-1 border rounded text-sm">
              Toggle to {chatMode === "announcement" ? "Open chat" : "Announcement"}
            </button>
          )}
          <button onClick={() => onClose && onClose()} className="px-2 py-1 border rounded text-sm">Close</button>
        </div>
      </div>

      <div className="h-80 overflow-y-auto border p-3 rounded bg-slate-50 dark:bg-slate-900">
        {messages.length === 0 && (
          <div className="text-sm text-gray-500">No messages yet</div>
        )}
        {messages.map((m, idx) => {
          const isMe = String(m.sender) === String(user?.id);
          const name = m.senderName || (m.sender && m.sender.name) || (isMe ? user.name : "Unknown");
          const email = m.senderEmail || (m.sender && m.sender.email) || (isMe ? user.email : "");
          return (
            <div key={idx} className={`mb-3 ${isMe ? "text-right" : "text-left"}`}>
              <div className={`inline-block px-3 py-2 rounded max-w-[80%] ${isMe ? "bg-blue-600 text-white" : "bg-gray-200 dark:bg-gray-700 dark:text-white"}`}>
                <div className="text-sm font-semibold">{name}{email ? ` — ${email}` : ""}</div>
                <div className="mt-1 text-sm">{m.text}</div>
                {m.attachmentUrl && (
                  <div className="mt-2">
                    <a href={m.attachmentUrl} target="_blank" rel="noreferrer" className="text-xs underline">
                      View attachment
                    </a>
                  </div>
                )}
                <div className="text-xs opacity-60 mt-1">{m.timestamp ? new Date(m.timestamp).toLocaleString() : ""}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="flex gap-2 mt-3">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          className="flex-1 p-2 border rounded dark:bg-slate-700 dark:text-white"
          placeholder={chatMode === "announcement" && !isPoster ? "Announcement mode — only poster can send" : "Write a message..."}
          disabled={sending || (chatMode === "announcement" && !isPoster)}
        />
        <input
          value={attachmentUrl}
          onChange={(e) => setAttachmentUrl(e.target.value)}
          placeholder="Attachment URL (optional)"
          className="w-56 p-2 border rounded dark:bg-slate-700 dark:text-white"
        />
        <button onClick={sendMessage} disabled={sending || (chatMode === "announcement" && !isPoster)} className="px-4 py-2 bg-blue-600 text-white rounded">
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}
